import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { CalendarDayCell } from "./CalendarDayCell"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROW_HEIGHT = 44
const WEEKS_BEFORE = 4
const WEEKS_AFTER = 1040 // ~20 years — effectively limitless (smoke detectors every 10 years)
const OVERSCAN = 8
const SCROLL_IDLE_DELAY = 150
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InfiniteCalendarProps {
  selectedDate?: dayjs.Dayjs
  onSelect: (date: dayjs.Dayjs) => void
  /**
   * Minimum selectable date.
   * - `undefined` = no restriction (all dates selectable, including past)
   * - `dayjs(...)` = only dates on or after this date are selectable
   * Pass `dayjs()` explicitly if you want "today" as minimum.
   */
  minDate?: dayjs.Dayjs
  /** Height of the calendar viewport in px */
  height?: number
  className?: string
  /** Map of "YYYY-MM-DD" → count for multi-select highlight */
  highlightedDates?: Map<string, number>
}

/** Either a month header row or a week row */
interface CalendarRow {
  type: "month-header" | "week"
  /** For month headers, the month label ("February 2026") */
  monthLabel?: string
  /** For week rows, the 7 days in this week */
  days?: dayjs.Dayjs[]
  /** The month this row belongs to (for context) */
  month: number
  /** The year this row belongs to */
  year: number
}

// ---------------------------------------------------------------------------
// Row generator
// ---------------------------------------------------------------------------

function generateCalendarRows(
  startDate: dayjs.Dayjs,
  totalWeeks: number,
): CalendarRow[] {
  const rows: CalendarRow[] = []
  let currentWeekStart = startDate.startOf("week") // dayjs default: depends on locale

  // Ensure Monday-based week start (ISO)
  // dayjs startOf("week") uses locale. For ISO Monday, we adjust.
  const day = startDate.day()
  const mondayOffset = day === 0 ? -6 : 1 - day
  currentWeekStart = startDate.add(mondayOffset, "day").startOf("day")

  // Adjust to start WEEKS_BEFORE weeks before today
  currentWeekStart = currentWeekStart.subtract(WEEKS_BEFORE, "week")

  let lastMonth = -1
  let lastYear = -1

  for (let w = 0; w < totalWeeks; w++) {
    const days: dayjs.Dayjs[] = []
    for (let d = 0; d < 7; d++) {
      days.push(currentWeekStart.add(d, "day"))
    }

    // Check if we need a month header before this week
    // Insert header when any day in the first half of the week starts a new month
    const firstDay = days[0]!
    const firstDayMonth = firstDay.month()
    const firstDayYear = firstDay.year()

    // Find if this week contains the 1st of a new month
    const hasNewMonth = days.some((d) => d.date() === 1)
    const newMonthDay = days.find((d) => d.date() === 1)

    if (
      hasNewMonth &&
      newMonthDay &&
      (newMonthDay.month() !== lastMonth || newMonthDay.year() !== lastYear)
    ) {
      const m = newMonthDay.month()
      const y = newMonthDay.year()
      rows.push({
        type: "month-header",
        monthLabel: newMonthDay.format("MMMM YYYY"),
        month: m,
        year: y,
      })
      lastMonth = m
      lastYear = y
    } else if (
      w === 0 &&
      (firstDayMonth !== lastMonth || firstDayYear !== lastYear)
    ) {
      // First row — always show the month header
      rows.push({
        type: "month-header",
        monthLabel: firstDay.format("MMMM YYYY"),
        month: firstDayMonth,
        year: firstDayYear,
      })
      lastMonth = firstDayMonth
      lastYear = firstDayYear
    }

    rows.push({
      type: "week",
      days,
      month: lastMonth,
      year: lastYear,
    })

    currentWeekStart = currentWeekStart.add(1, "week")
  }

  return rows
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Infinite scrolling calendar — Things 3 style.
 *
 * Uses @tanstack/react-virtual for efficient rendering.
 * Shows continuous weeks with month headers inserted when months change.
 * Monday-based week start (ISO).
 */
export const InfiniteCalendar = memo(function InfiniteCalendar({
  selectedDate,
  onSelect,
  minDate,
  height = 240,
  className,
  highlightedDates,
}: InfiniteCalendarProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [visibleMonth, setVisibleMonth] = useState("")

  const today = useMemo(() => dayjs().startOf("day"), [])
  // undefined minDate = no restriction (all dates selectable)
  const min = useMemo(
    () => (minDate ? minDate.startOf("day") : undefined),
    [minDate],
  )

  // Generate all calendar rows
  const rows = useMemo(
    () => generateCalendarRows(today, WEEKS_BEFORE + WEEKS_AFTER),
    [today],
  )

  // Find the index of the week containing today (for initial scroll)
  const todayWeekIndex = useMemo(() => {
    return rows.findIndex(
      (row) =>
        row.type === "week" && row.days?.some((d) => d.isSame(today, "day")),
    )
  }, [rows, today])

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      rows[index]!.type === "month-header" ? 28 : ROW_HEIGHT,
    overscan: OVERSCAN,
    initialOffset: Math.max(0, todayWeekIndex - 2) * ROW_HEIGHT,
  })

  // Track scroll state for month overlay
  const handleScroll = useCallback(() => {
    setIsScrolling(true)
    if (scrollIdleTimer.current) clearTimeout(scrollIdleTimer.current)
    scrollIdleTimer.current = setTimeout(() => {
      setIsScrolling(false)
    }, SCROLL_IDLE_DELAY)

    // Update visible month based on middle of viewport
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop
      const middleOffset = scrollTop + height / 2
      const items = virtualizer.getVirtualItems()
      const middleItem = items.find(
        (item) =>
          item.start <= middleOffset && item.start + item.size > middleOffset,
      )
      if (middleItem) {
        const row = rows[middleItem.index]
        if (row) {
          const label =
            row.type === "month-header"
              ? (row.monthLabel ?? "")
              : dayjs().month(row.month).year(row.year).format("MMMM YYYY")
          if (label && label !== visibleMonth) setVisibleMonth(label)
        }
      }
    }
  }, [height, rows, virtualizer, visibleMonth])

  useEffect(() => {
    return () => {
      if (scrollIdleTimer.current) clearTimeout(scrollIdleTimer.current)
    }
  }, [])

  return (
    <div className={cn("relative select-none", className)}>
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-border/30 pb-1.5">
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            className="flex items-center justify-center text-xs font-medium text-muted-foreground/60"
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Scrollable calendar body */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-vaul-no-drag
        className="overflow-y-auto scrollbar-thin"
        style={{ height }}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index]
            if (!row) return null

            if (row.type === "month-header") {
              return (
                <div
                  key={`header-${virtualRow.index}`}
                  className="absolute left-0 right-0 flex items-center bg-background"
                  style={{
                    top: virtualRow.start,
                    height: virtualRow.size,
                  }}
                >
                  <span className="px-1 text-xs font-semibold text-muted-foreground">
                    {row.monthLabel}
                  </span>
                </div>
              )
            }

            // Week row
            return (
              <div
                key={`week-${virtualRow.index}`}
                className="absolute left-0 right-0 grid grid-cols-7"
                style={{
                  top: virtualRow.start,
                  height: virtualRow.size,
                }}
              >
                {row.days?.map((dayDate) => (
                  <CalendarDayCell
                    key={dayDate.format("YYYY-MM-DD")}
                    date={dayDate}
                    isToday={dayDate.isSame(today, "day")}
                    isSelected={
                      selectedDate ? dayDate.isSame(selectedDate, "day") : false
                    }
                    isPast={dayDate.isBefore(today, "day")}
                    isDisabled={dayDate.isBefore(min, "day")}
                    isCurrentMonth={
                      dayDate.month() === row.month &&
                      dayDate.year() === row.year
                    }
                    highlightCount={highlightedDates?.get(dayDate.format("YYYY-MM-DD")) ?? 0}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            )
          })}
        </div>
      </div>

      {/* Month overlay during scroll */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 z-20 flex items-center justify-center transition-opacity duration-200",
          isScrolling ? "opacity-100" : "opacity-0",
        )}
      >
        <span className="rounded-lg border border-border bg-popover px-3 py-1.5 text-sm font-semibold text-foreground shadow-lg">
          {visibleMonth}
        </span>
      </div>
    </div>
  )
})
